import { starterTypeLabel } from '../../i18n/labels';
import type { StarterType } from '../../types';

/** UCI rider count per starter type. */
const RIDERS: Readonly<Record<StarterType, number>> = { single: 1, pair: 2, team4: 4, team6: 6 };

const BIKES_W = 28;
const BIKES_H = 20;
/** One bicycle glyph's native size, wheels included — the coordinate space
 *  every echo wheel below is measured in too, so they align with the full
 *  bike's own wheels. */
const BIKE_W = 12;
const BIKE_H = 9.8;
const WHEEL_R = 2.3;
const WHEEL_CY = 7.3;
/** Horizontal offset between stacked bikes — smaller than the wheel
 *  diameter so consecutive wheels overlap rather than just touch. */
const STACK_STEP = 4.2;

function Bike() {
  return (
    <>
      <circle cx={2.5} cy={WHEEL_CY} r={WHEEL_R} />
      <circle cx={9.5} cy={WHEEL_CY} r={WHEEL_R} />
      <path d={`M2.5 ${WHEEL_CY}L5.6 2.4L7.8 2.4L9.5 ${WHEEL_CY}M5.6 2.4L4.3 ${WHEEL_CY}`} />
    </>
  );
}

/** Just the leading (left) half of a wheel — the "there's another bike
 *  behind this one" hint used for every rider but the last. */
function HalfWheel({ cx }: { cx: number }) {
  const top = (WHEEL_CY - WHEEL_R).toFixed(2);
  const bottom = (WHEEL_CY + WHEEL_R).toFixed(2);
  return <path d={`M${cx.toFixed(2)} ${top}A${WHEEL_R} ${WHEEL_R} 0 0 0 ${cx.toFixed(2)} ${bottom}`} />;
}

/** UCI rider count (1/2/4/6) drawn as bicycles stacked behind one another —
 *  every rider but the last is just a half wheel peeking out, then a full
 *  bicycle in front — instead of spelling out "{2er (Zweier)}" etc. in the
 *  programme list, which ran too long next to the figure count and points.
 *  Screen readers still get the starter type via `aria-label` since the
 *  glyph itself is decorative. */
export function BikesIcon({ type }: { type: StarterType }) {
  const count = RIDERS[type];
  const echoCount = count - 1;
  const nativeW = echoCount * STACK_STEP + BIKE_W;
  const scale = Math.min(BIKES_W / nativeW, BIKES_H / BIKE_H);
  const tx = (BIKES_W - nativeW * scale) / 2;
  const ty = (BIKES_H - BIKE_H * scale) / 2;

  return (
    <svg
      className="h-5 w-7 shrink-0 text-muted-foreground"
      viewBox={`0 0 ${BIKES_W} ${BIKES_H}`}
      role="img"
      aria-label={starterTypeLabel(type)}
      stroke="currentColor"
      fill="none"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <g transform={`translate(${tx.toFixed(2)},${ty.toFixed(2)}) scale(${scale.toFixed(3)})`}>
        {Array.from({ length: echoCount }, (_, i) => (
          <HalfWheel key={i} cx={i * STACK_STEP + WHEEL_R} />
        ))}
        <g transform={`translate(${(echoCount * STACK_STEP).toFixed(2)},0)`}>
          <Bike />
        </g>
      </g>
    </svg>
  );
}
